/**
 * Partner Invitation Templates
 *
 * Templates for the B1-to-B2 partner invitation and onboarding flow using IntakeWizardScroll.
 *
 * @see Issue #484 - B1-to-B2 Partner Invitation & Onboarding Flow
 * @see Business Portal - Invite Partner wizard (B1 side)
 * @see Auth Portal - Partner Onboarding wizard (B2 side)
 */

import { PartnerType } from '../enums/partner.enums';
import { IntakeDisplayMode, type IntakeFlowTemplate, type IntakeStepTemplate } from './types';

// =============================================================================
// TEMPLATE IDS
// =============================================================================

export const PARTNER_INVITATION_TEMPLATE_IDS = {
  INVITE_PARTNER: 'tpl-invite-partner',
  PARTNER_ONBOARDING: 'tpl-partner-onboarding',
} as const;

// =============================================================================
// B1 FLOW: INVITE PARTNER TEMPLATE
// =============================================================================

/**
 * Step 1: Partner Information
 * Collects basic partner company details
 */
const createPartnerInfoStep = (): IntakeStepTemplate => ({
  id: 'step-partner-info',
  order: 0,
  name: 'Partner Information',
  description: 'Enter the partner company details',
  icon: 'Business',
  requiresAuth: false,
  fields: [
    {
      id: 'field-company-name',
      name: 'invitedCompanyName',
      label: 'Partner Company Name',
      type: 'text',
      required: true,
      placeholder: 'Partner Co.',
      helpText: 'The legal name of the partner company',
      validation: {
        minLength: 2,
        maxLength: 255,
      },
      width: 6,
    },
    {
      id: 'field-contact-name',
      name: 'invitedContactName',
      label: 'Primary Contact Name',
      type: 'text',
      required: true,
      placeholder: 'Jane Smith',
      helpText: 'The person who will receive the invitation',
      width: 6,
    },
    {
      id: 'field-contact-email',
      name: 'invitedEmail',
      label: 'Contact Email',
      type: 'email',
      required: true,
      placeholder: 'jane@partner.com',
      helpText: 'Invitation will be sent to this email',
      validation: {
        pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
        messages: {
          pattern: 'Please enter a valid email address',
        },
      },
      width: 6,
    },
    {
      id: 'field-contact-phone',
      name: 'invitedPhone',
      label: 'Contact Phone',
      type: 'phone',
      required: false,
      placeholder: '+1 (555) 123-4567',
      width: 6,
    },
  ],
});

/**
 * Step 2: Partnership Type
 * Select partner type and agreement direction
 */
const createPartnershipTypeStep = (): IntakeStepTemplate => ({
  id: 'step-partnership-type',
  order: 1,
  name: 'Partnership Type',
  description: 'Define the type of partnership',
  icon: 'Handshake',
  requiresAuth: false,
  fields: [
    {
      id: 'field-partner-type',
      name: 'partnerType',
      label: 'Partner Type',
      type: 'radio-cards',
      required: true,
      defaultValue: PartnerType.REFERRAL,
      radioCardsConfig: {
        options: [
          {
            value: PartnerType.REFERRAL,
            label: 'Referral Partner',
            description:
              'Partner refers clients to you; the borrower sees your brand. Partner earns commission on funded deals.',
            icon: 'TrendingUp',
          },
          {
            value: PartnerType.WHITE_LABEL,
            label: 'White-Label Partner',
            description:
              'White-labeled: partner keeps borrowers on their own account and branding. You process the loan behind the scenes — borrower never sees your brand.',
            icon: 'Storefront',
          },
        ],
        columns: 2,
      },
      width: 12,
    },
    {
      id: 'field-agreement-type',
      name: 'agreementType',
      label: 'Agreement Direction',
      type: 'radio-cards',
      required: true,
      defaultValue: 'ONE_WAY',
      radioCardsConfig: {
        options: [
          {
            value: 'ONE_WAY',
            label: 'One-Way',
            description: 'Partner refers to you only',
            icon: 'ArrowForward',
          },
          {
            value: 'TWO_WAY',
            label: 'Two-Way (Mutual)',
            description: 'Both parties can refer to each other',
            icon: 'SwapHoriz',
          },
        ],
        columns: 2,
      },
      width: 12,
    },
  ],
});

/**
 * Step 3: Commission Terms
 * Configure commission rates and payment terms
 */
const createCommissionTermsStep = (): IntakeStepTemplate => ({
  id: 'step-commission-terms',
  order: 2,
  name: 'Commission Terms',
  description:
    'Commission you pay the partner when they refer a deal. For mutual agreements, a second commission covers the reverse direction.',
  icon: 'Payments',
  requiresAuth: false,
  fields: [
    {
      id: 'field-commission-type',
      name: 'commissionType',
      label: 'Commission Type (you pay partner)',
      type: 'select',
      required: false,
      // Three commission types are the operator-tested set. Dropped from
      // the prior four: `PERCENTAGE_REVENUE` was ambiguous (revenue from
      // which side?) and `TIERED_VOLUME` was rarely used + carried a
      // distinct payload shape that doesn't fit the current proto. Added
      // `PERCENTAGE_OF_ORIGINATION` so partners who get paid out of the
      // lender's origination fee have an unambiguous option (Dave/prod
      // review, 2026-05-15).
      defaultValue: 'PERCENTAGE_LOAN',
      options: [
        { value: 'PERCENTAGE_LOAN', label: '% of Loan Amount (bps OK)' },
        { value: 'PERCENTAGE_OF_ORIGINATION', label: '% of Origination Fee' },
        { value: 'FLAT_AMOUNT', label: 'Flat Fee ($/Deal)' },
      ],
      width: 6,
    },
    {
      id: 'field-commission-rate',
      name: 'commissionRate',
      label: 'Commission Rate (%)',
      type: 'number',
      required: false,
      placeholder: '2.5',
      // The rate is always entered as a percent (e.g. 2.5 = 2.5%). For
      // very small rates expressed in bps (10 bps = 0.10%), enter 0.10
      // here. UI may render a bps display toggle on top of the same
      // underlying field — both bp and % serializations write the same
      // value. Max raised from 10 to 25 to cover origination-fee splits
      // (a 25% split of a 1% origination fee = 25 bps to the partner).
      helpText: 'Percent of the chosen base (e.g. 2.5 = 2.5%). 10 bps = 0.10%.',
      validation: {
        min: 0,
        max: 25,
        messages: {
          min: 'Commission rate must be at least 0%',
          max: 'Commission rate cannot exceed 25%',
        },
      },
      conditionalDisplay: {
        field: 'commissionType',
        operator: 'in',
        value: ['PERCENTAGE_LOAN', 'PERCENTAGE_OF_ORIGINATION'],
      },
      width: 6,
    },
    {
      id: 'field-flat-amount',
      name: 'flatAmount',
      label: 'Flat Amount ($)',
      type: 'currency',
      required: false,
      placeholder: '500',
      helpText: 'Fixed amount per deal',
      conditionalDisplay: {
        field: 'commissionType',
        operator: 'equals',
        value: 'FLAT_AMOUNT',
      },
      width: 6,
    },
    {
      id: 'field-commission-trigger',
      name: 'commissionTrigger',
      label: 'Commission Earned When',
      type: 'select',
      required: false,
      defaultValue: 'FUNDING',
      options: [
        { value: 'SUBMISSION', label: 'Deal Submitted' },
        { value: 'APPROVAL', label: 'Deal Approved' },
        { value: 'FUNDING', label: 'Deal Funded' },
        { value: 'FIRST_PAYMENT', label: 'First Payment Received' },
      ],
      helpText: 'When the partner earns their commission',
      width: 6,
    },
    {
      id: 'field-payment-terms',
      name: 'paymentTerms',
      label: 'Payment Terms',
      type: 'select',
      required: false,
      defaultValue: 'NET_30',
      options: [
        { value: 'IMMEDIATE', label: 'Immediate' },
        { value: 'NET_15', label: 'Net 15 Days' },
        { value: 'NET_30', label: 'Net 30 Days' },
        { value: 'NET_45', label: 'Net 45 Days' },
        { value: 'NET_60', label: 'Net 60 Days' },
      ],
      helpText: 'When you pay the partner after commission is earned',
      width: 6,
    },

    // ─────────────────────────────────────────────────────────────────────
    // Inbound commission (only shown for TWO_WAY agreements — the partner
    // pays the lender when they receive a referral in the reverse direction.
    // Each field can differ from the outbound equivalent: partners often
    // pay a different rate, format (flat vs %), or trigger.
    // ─────────────────────────────────────────────────────────────────────
    {
      id: 'field-inbound-commission-type',
      name: 'inboundCommissionType',
      label: 'Commission Type (partner pays you)',
      type: 'select',
      required: false,
      // Mirror the outbound options set (Dave/prod review 2026-05-15).
      // `PERCENTAGE_REVENUE` was dropped (ambiguous direction) and
      // `PERCENTAGE_OF_ORIGINATION` added so the inbound side has the
      // same three choices as outbound.
      defaultValue: 'PERCENTAGE_LOAN',
      options: [
        { value: 'PERCENTAGE_LOAN', label: '% of Loan Amount (bps OK)' },
        { value: 'PERCENTAGE_OF_ORIGINATION', label: '% of Origination Fee' },
        { value: 'FLAT_AMOUNT', label: 'Flat Fee ($/Deal)' },
      ],
      conditionalDisplay: {
        field: 'agreementType',
        operator: 'equals',
        value: 'TWO_WAY',
      },
      helpText: 'Reverse direction: what the partner pays you when you refer a deal to them.',
      width: 6,
    },
    {
      id: 'field-inbound-commission-rate',
      name: 'inboundCommissionRate',
      label: 'Inbound Commission Rate (%)',
      type: 'number',
      required: false,
      placeholder: '2.5',
      helpText: 'Percentage the partner pays you (can differ from outbound).',
      validation: { min: 0, max: 10 },
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'agreementType', operator: 'equals', value: 'TWO_WAY' },
          { field: 'inboundCommissionType', operator: 'in', value: ['PERCENTAGE_LOAN', 'PERCENTAGE_OF_ORIGINATION'] },
        ],
      },
      width: 6,
    },
    {
      id: 'field-inbound-flat-amount',
      name: 'inboundFlatAmount',
      label: 'Inbound Flat Amount ($)',
      type: 'currency',
      required: false,
      placeholder: '500',
      helpText: 'Fixed amount the partner pays you per deal.',
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'agreementType', operator: 'equals', value: 'TWO_WAY' },
          { field: 'inboundCommissionType', operator: 'equals', value: 'FLAT_AMOUNT' },
        ],
      },
      width: 6,
    },
    {
      id: 'field-inbound-commission-trigger',
      name: 'inboundCommissionTrigger',
      label: 'Inbound Commission Earned When',
      type: 'select',
      required: false,
      defaultValue: 'FUNDING',
      options: [
        { value: 'SUBMISSION', label: 'Deal Submitted' },
        { value: 'APPROVAL', label: 'Deal Approved' },
        { value: 'FUNDING', label: 'Deal Funded' },
        { value: 'FIRST_PAYMENT', label: 'First Payment Received' },
      ],
      helpText: 'When you earn your commission on reverse referrals.',
      conditionalDisplay: {
        field: 'agreementType',
        operator: 'equals',
        value: 'TWO_WAY',
      },
      width: 6,
    },
    {
      id: 'field-inbound-payment-terms',
      name: 'inboundPaymentTerms',
      label: 'Inbound Payment Terms',
      type: 'select',
      required: false,
      defaultValue: 'NET_30',
      options: [
        { value: 'IMMEDIATE', label: 'Immediate' },
        { value: 'NET_15', label: 'Net 15 Days' },
        { value: 'NET_30', label: 'Net 30 Days' },
        { value: 'NET_45', label: 'Net 45 Days' },
        { value: 'NET_60', label: 'Net 60 Days' },
      ],
      helpText: 'When the partner pays you after commission is earned.',
      conditionalDisplay: {
        field: 'agreementType',
        operator: 'equals',
        value: 'TWO_WAY',
      },
      width: 6,
    },
  ],
});

/**
 * Step 4: Agreement Terms
 * Set duration, exclusivity, and restrictions
 */
const createAgreementTermsStep = (): IntakeStepTemplate => ({
  id: 'step-agreement-terms',
  order: 3,
  name: 'Agreement Terms',
  description: 'Configure agreement duration and restrictions',
  icon: 'Description',
  requiresAuth: false,
  fields: [
    {
      id: 'field-effective-date',
      name: 'effectiveDate',
      label: 'Effective Date',
      type: 'date',
      required: false,
      defaultValue: 'today',
      helpText: 'When the partnership agreement starts',
      width: 6,
    },
    {
      id: 'field-expiration-date',
      name: 'expirationDate',
      label: 'Expiration Date',
      type: 'date',
      required: false,
      helpText: 'Leave blank for evergreen agreement',
      width: 6,
    },
    {
      id: 'field-auto-renew',
      name: 'autoRenew',
      label: 'Auto-renew agreement annually',
      type: 'checkbox',
      required: false,
      defaultValue: true,
      width: 6,
    },
    {
      id: 'field-exclusivity',
      name: 'exclusivityScope',
      label: 'Exclusivity',
      type: 'select',
      required: false,
      defaultValue: 'NONE',
      options: [
        { value: 'NONE', label: 'Non-Exclusive' },
        { value: 'GEOGRAPHIC', label: 'Geographic Exclusivity' },
        { value: 'PRODUCT_TYPE', label: 'Product Exclusivity' },
        { value: 'FULL', label: 'Full Exclusivity' },
      ],
      helpText: 'Whether the partner has exclusive rights',
      width: 6,
    },
    {
      id: 'field-product-restrictions',
      name: 'productRestrictions',
      label: 'Product Restrictions',
      type: 'multi-select',
      required: false,
      helpText: 'Limit to specific loan products (empty = all products)',
      // Resolved from the tenant's configured loan products at runtime via
      // the wizard's `dynamicOptions['loan-products']` map. Stored value is
      // the loan product `id` (UUID).
      dynamicSelectConfig: {
        dataSource: 'loan-products',
        valueField: 'id',
        labelField: 'name',
        emptyMessage: 'No loan products configured',
        loadingMessage: 'Loading loan products…',
      },
      width: 6,
    },
    {
      id: 'field-state-restrictions',
      name: 'stateRestrictions',
      label: 'State Restrictions',
      type: 'multi-select',
      required: false,
      helpText: 'Limit to specific states (empty = all states)',
      dynamicSelectConfig: {
        dataSource: 'us-states',
        valueField: 'code',
        labelField: 'name',
      },
      width: 6,
    },
    {
      id: 'field-partner-visibility',
      name: 'partnerVisibility',
      label: 'Partner Visibility',
      type: 'select',
      required: false,
      defaultValue: 'STATS_ONLY',
      options: [
        { value: 'STATS_ONLY', label: 'Statistics Only' },
        { value: 'CASE_SUMMARY', label: 'Loan Summaries' },
        { value: 'FULL_DETAILS', label: 'Full Loan Details' },
      ],
      helpText: 'What the partner can see about referred deals',
      width: 6,
    },
  ],
});

/**
 * Step 5: E-Sign Template
 * Select the contract template for e-signature
 */
const createESignTemplateStep = (): IntakeStepTemplate => ({
  id: 'step-esign-template',
  order: 4,
  name: 'Contract Template',
  description: 'Select the contract template for this partnership',
  icon: 'Draw',
  requiresAuth: false,
  fields: [
    {
      id: 'field-template',
      name: 'documentTemplateId',
      label: 'Contract Template',
      type: 'dynamic-select',
      required: true,
      helpText: 'The partnership agreement template to use',
      dynamicSelectConfig: {
        dataSource: 'document-templates',
        valueField: 'id',
        labelField: 'name',
        filterBy: { category: 'partnership' },
        emptyMessage: 'No partnership templates available. Please create one first.',
        loadingMessage: 'Loading templates...',
      },
      width: 12,
    },
    {
      id: 'field-b1-signer-email',
      name: 'b1SignerEmail',
      label: 'Your Authorized Signer Email',
      type: 'email',
      required: true,
      helpText: 'Who will sign on behalf of your company',
      validation: {
        pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
      },
      width: 6,
    },
    {
      id: 'field-b1-signer-name',
      name: 'b1SignerName',
      label: 'Your Authorized Signer Name',
      type: 'text',
      required: true,
      placeholder: 'John Doe',
      width: 6,
    },
  ],
});

/**
 * Step 6: Review
 * Summary of all collected information
 */
const createInviteReviewStep = (): IntakeStepTemplate => ({
  id: 'step-review',
  order: 5,
  name: 'Review & Send',
  description: 'Review the invitation details before sending',
  icon: 'CheckCircle',
  requiresAuth: false,
  fields: [
    {
      id: 'field-summary',
      name: '_summary',
      label: '',
      type: 'summary',
      required: false,
      summaryConfig: {
        sections: [
          {
            title: 'Partner Information',
            fields: [
              { name: 'invitedCompanyName', label: 'Company Name', format: 'text' },
              { name: 'invitedContactName', label: 'Contact Name', format: 'text' },
              { name: 'invitedEmail', label: 'Email', format: 'text' },
              { name: 'invitedPhone', label: 'Phone', format: 'text' },
            ],
          },
          {
            title: 'Partnership Type',
            fields: [
              {
                name: 'partnerType',
                label: 'Type',
                format: 'lookup',
                lookupMap: {
                  [PartnerType.REFERRAL]: 'Referral Partner',
                  [PartnerType.WHITE_LABEL]: 'White-Label Partner',
                },
              },
              {
                name: 'agreementType',
                label: 'Direction',
                format: 'lookup',
                lookupMap: {
                  ONE_WAY: 'One-Way',
                  TWO_WAY: 'Two-Way (Mutual)',
                },
              },
            ],
          },
          {
            title: 'Commission Terms',
            fields: [
              { name: 'commissionRate', label: 'Commission Rate', format: 'percentage' },
              {
                name: 'commissionTrigger',
                label: 'Earned When',
                format: 'lookup',
                lookupMap: {
                  SUBMISSION: 'Deal Submitted',
                  APPROVAL: 'Deal Approved',
                  FUNDING: 'Deal Funded',
                  FIRST_PAYMENT: 'First Payment',
                },
              },
              {
                name: 'paymentTerms',
                label: 'Payment',
                format: 'lookup',
                lookupMap: {
                  IMMEDIATE: 'Immediate',
                  NET_15: 'Net 15',
                  NET_30: 'Net 30',
                  NET_45: 'Net 45',
                  NET_60: 'Net 60',
                },
              },
            ],
          },
          {
            title: 'Agreement Terms',
            fields: [
              { name: 'effectiveDate', label: 'Effective', format: 'date' },
              { name: 'expirationDate', label: 'Expires', format: 'date' },
              { name: 'autoRenew', label: 'Auto-Renew', format: 'boolean' },
              {
                name: 'partnerVisibility',
                label: 'Visibility',
                format: 'lookup',
                lookupMap: {
                  STATS_ONLY: 'Statistics Only',
                  CASE_SUMMARY: 'Loan Summaries',
                  FULL_DETAILS: 'Full Loan Details',
                },
              },
            ],
          },
        ],
        showEditButtons: true,
      },
      width: 12,
    },
  ],
});

/**
 * Partner Invitation Template (B1 Flow)
 */
export const invitePartnerTemplate: IntakeFlowTemplate = {
  id: PARTNER_INVITATION_TEMPLATE_IDS.INVITE_PARTNER,
  name: 'Invite Partner',
  description: 'Invite a new partner company to join your referral network',
  category: 'partnerships',
  icon: 'PersonAdd',
  color: '#0078D7', // Business Portal blue
  tags: ['partner', 'invitation', 'referral', 'b2b'],
  complexity: 'intermediate',
  industryFit: ['lending', 'finance'],
  data: {
    intakeType: 'partner_invitation',
    version: '1.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createPartnerInfoStep(),
      createPartnershipTypeStep(),
      createCommissionTermsStep(),
      createAgreementTermsStep(),
      createESignTemplateStep(),
      createInviteReviewStep(),
    ],
    complianceQuestions: [
      {
        id: 'authorization',
        question: 'I am authorized to invite partners on behalf of my company.',
        requiredAnswer: true,
        blockingMessage: 'You must be authorized to invite partners.',
      },
    ],
    exitBehavior: 'custom',
    customExitUrl: '/partnerships/invitations',
    showEstimatedTime: true,
    estimatedTimeMinutes: 5,
    successMessage: 'Partner invitation sent successfully!',
  },
};

// =============================================================================
// B2 FLOW: PARTNER ONBOARDING TEMPLATE
// =============================================================================

/**
 * Step 1: Welcome & Verify
 * Shows invitation details and verifies intent
 */
const createWelcomeStep = (): IntakeStepTemplate => ({
  id: 'step-welcome',
  order: 0,
  name: 'Welcome',
  description: 'Review your partnership invitation',
  icon: 'Celebration',
  requiresAuth: false,
  fields: [
    {
      id: 'field-welcome-message',
      name: '_welcomeMessage',
      label: '',
      type: 'info-card',
      required: false,
      infoCardConfig: {
        variant: 'highlighted',
        icon: 'Handshake',
        title: "You've been invited to partner with {{invitingCompanyName}}",
        description: 'Complete this form to set up your partner account and start referring deals.',
      },
      width: 12,
    },
    {
      id: 'field-terms-preview',
      name: '_termsPreview',
      label: 'Partnership Terms',
      type: 'readonly-summary',
      required: false,
      readonlySummaryConfig: {
        fields: [
          { name: 'partnerType', label: 'Partnership Type' },
          { name: 'commissionRate', label: 'Commission Rate' },
          { name: 'paymentTerms', label: 'Payment Terms' },
        ],
      },
      width: 12,
    },
  ],
});

/**
 * Step 2: Company Profile
 * Collect B2 company information
 */
const createCompanyProfileStep = (): IntakeStepTemplate => ({
  id: 'step-company-profile',
  order: 1,
  name: 'Company Profile',
  description: 'Enter your company information',
  icon: 'Business',
  requiresAuth: false,
  fields: [
    {
      id: 'field-company-name',
      name: 'companyName',
      label: 'Legal Company Name',
      type: 'text',
      required: true,
      helpText: 'As registered with your state',
      validation: {
        minLength: 2,
        maxLength: 255,
      },
      width: 6,
    },
    {
      id: 'field-nmls',
      name: 'nmlsCompanyId',
      label: 'NMLS Company ID',
      type: 'text',
      required: false,
      placeholder: '123456',
      helpText: 'Required if you originate or broker loans',
      validation: {
        pattern: '^[0-9]{5,10}$',
        messages: {
          pattern: 'NMLS ID must be 5-10 digits',
        },
      },
      width: 6,
    },
    {
      id: 'field-website',
      name: 'website',
      label: 'Company Website',
      type: 'url',
      required: false,
      placeholder: 'https://yourcompany.com',
      width: 6,
    },
    {
      id: 'field-phone',
      name: 'phone',
      label: 'Company Phone',
      type: 'phone',
      required: true,
      width: 6,
    },
    {
      id: 'field-address-street',
      name: 'addressStreet',
      label: 'Street Address',
      type: 'text',
      required: true,
      placeholder: '123 Main Street',
      width: 12,
    },
    {
      id: 'field-address-city',
      name: 'addressCity',
      label: 'City',
      type: 'text',
      required: true,
      width: 4,
    },
    {
      id: 'field-address-state',
      name: 'addressState',
      label: 'State',
      type: 'select',
      required: true,
      options: [
        { value: 'AL', label: 'Alabama' },
        { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' },
        { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' },
        { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' },
        { value: 'DE', label: 'Delaware' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' },
        { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' },
        { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' },
        { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' },
        { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' },
        { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' },
        { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' },
        { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' },
        { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' },
        { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' },
        { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' },
        { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' },
        { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' },
        { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' },
        { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' },
        { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' },
        { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' },
        { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' },
        { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' },
        { value: 'WY', label: 'Wyoming' },
        { value: 'DC', label: 'District of Columbia' },
      ],
      width: 4,
    },
    {
      id: 'field-address-zip',
      name: 'addressZip',
      label: 'ZIP Code',
      type: 'text',
      required: true,
      placeholder: '12345',
      validation: {
        pattern: '^[0-9]{5}(-[0-9]{4})?$',
        messages: {
          pattern: 'Invalid ZIP code format',
        },
      },
      width: 4,
    },
  ],
});

/**
 * Step 3: Licensing (Conditional)
 * Upload license documents if required
 */
const createLicensingStep = (): IntakeStepTemplate => ({
  id: 'step-licensing',
  order: 2,
  name: 'Licensing',
  description: 'Upload your licensing documents',
  icon: 'VerifiedUser',
  requiresAuth: false,
  conditionalDisplay: {
    field: 'nmlsCompanyId',
    operator: 'is_not_empty',
  },
  fields: [
    {
      id: 'field-license-docs',
      name: 'licenseDocuments',
      label: 'License Documents',
      type: 'file-upload',
      required: true,
      helpText: 'Upload your state licenses and NMLS certificate',
      fileUploadConfig: {
        accept: '.pdf,.jpg,.jpeg,.png',
        maxFiles: 5,
        maxSizeMB: 10,
        uploadEndpoint: '/api/upload/license',
      },
      width: 12,
    },
    {
      id: 'field-license-states',
      name: 'licensedStates',
      label: 'Licensed States',
      type: 'multi-select',
      required: true,
      helpText: 'Select all states where you hold an active license',
      dynamicSelectConfig: {
        dataSource: 'us-states',
        valueField: 'code',
        labelField: 'name',
      },
      width: 12,
    },
  ],
});

/**
 * Step 4: Admin Account
 * Create the primary admin user for B2
 */
const createAdminAccountStep = (): IntakeStepTemplate => ({
  id: 'step-admin-account',
  order: 3,
  name: 'Admin Account',
  description: 'Set up your administrator account',
  icon: 'Person',
  requiresAuth: false,
  fields: [
    {
      id: 'field-admin-first-name',
      name: 'adminFirstName',
      label: 'First Name',
      type: 'text',
      required: true,
      width: 6,
    },
    {
      id: 'field-admin-last-name',
      name: 'adminLastName',
      label: 'Last Name',
      type: 'text',
      required: true,
      width: 6,
    },
    {
      id: 'field-admin-email',
      name: 'adminEmail',
      label: 'Email',
      type: 'email',
      required: true,
      disabled: true, // Pre-filled from invitation
      helpText: 'This will be your login email',
      width: 6,
    },
    {
      id: 'field-admin-phone',
      name: 'adminPhone',
      label: 'Mobile Phone',
      type: 'phone',
      required: false,
      helpText: 'For two-factor authentication',
      width: 6,
    },
    {
      id: 'field-password',
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      helpText: 'Min 8 characters with uppercase, lowercase, and number',
      validation: {
        minLength: 8,
        pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$',
        messages: {
          minLength: 'Password must be at least 8 characters',
          pattern: 'Must include uppercase, lowercase, and a number',
        },
      },
      width: 6,
    },
    {
      id: 'field-confirm-password',
      name: 'confirmPassword',
      label: 'Confirm Password',
      type: 'password',
      required: true,
      validation: {
        matchField: 'password',
        messages: {
          matchField: 'Passwords must match',
        },
      },
      width: 6,
    },
  ],
});

/**
 * Step 5: Review Terms
 * Review partnership terms before signing
 */
const createReviewTermsStep = (): IntakeStepTemplate => ({
  id: 'step-review-terms',
  order: 4,
  name: 'Review Terms',
  description: 'Review the partnership agreement terms',
  icon: 'Gavel',
  requiresAuth: false,
  fields: [
    {
      id: 'field-agreement-preview',
      name: '_agreementPreview',
      label: 'Partnership Agreement',
      type: 'document-preview',
      required: false,
      documentPreviewConfig: {
        sourceField: 'agreementPdfUrl', // Generated from template
        height: 400,
      },
      width: 12,
    },
    {
      id: 'field-terms-summary',
      name: '_termsSummary',
      label: 'Key Terms',
      type: 'summary',
      required: false,
      summaryConfig: {
        sections: [
          {
            title: 'Commission Structure',
            fields: [
              { name: 'commissionRate', label: 'Rate', format: 'percentage' },
              { name: 'commissionTrigger', label: 'Earned When', format: 'text' },
              { name: 'paymentTerms', label: 'Payment', format: 'text' },
            ],
          },
          {
            title: 'Agreement Duration',
            fields: [
              { name: 'effectiveDate', label: 'Starts', format: 'date' },
              { name: 'expirationDate', label: 'Expires', format: 'date' },
            ],
          },
        ],
        showEditButtons: false,
      },
      width: 12,
    },
  ],
});

/**
 * Step 6: E-Sign
 * Embedded DocuSeal signing
 */
const createESignStep = (): IntakeStepTemplate => ({
  id: 'step-esign',
  order: 5,
  name: 'Sign Agreement',
  description: 'Sign the partnership agreement electronically',
  icon: 'Draw',
  requiresAuth: false,
  fields: [
    {
      id: 'field-esign-embed',
      name: '_esignEmbed',
      label: '',
      type: 'esign-embed',
      required: false, // Signing happens post-onboarding (PENDING_SIGNATURE status) — must not block business creation
      esignEmbedConfig: {
        envelopeIdField: 'signingEnvelopeId',
        recipientRole: 'PARTY_B',
        height: 600,
        onComplete: 'advance', // Auto-advance to next step
      },
      width: 12,
    },
  ],
});

/**
 * Step 7: Complete
 * Success message and next steps
 */
const createCompleteStep = (): IntakeStepTemplate => ({
  id: 'step-complete',
  order: 6,
  name: 'Complete',
  description: 'Your partner account is ready',
  icon: 'CheckCircle',
  requiresAuth: false,
  fields: [
    {
      id: 'field-success',
      name: '_success',
      label: '',
      type: 'success-card',
      required: false,
      successCardConfig: {
        title: 'Welcome to the Partner Network!',
        description: 'Your account has been created and the partnership agreement is now active.',
        actions: [
          {
            label: 'Go to Dashboard',
            href: '/dashboard',
            variant: 'primary',
          },
          {
            label: 'View Referral Link',
            href: '/partnerships/referral-link',
            variant: 'secondary',
          },
        ],
        confetti: true,
      },
      width: 12,
    },
  ],
});

/**
 * Partner Onboarding Template (B2 Flow)
 */
export const partnerOnboardingTemplate: IntakeFlowTemplate = {
  id: PARTNER_INVITATION_TEMPLATE_IDS.PARTNER_ONBOARDING,
  name: 'Partner Onboarding',
  description: 'Complete your partner account setup',
  category: 'onboarding',
  icon: 'Handshake',
  color: '#0078D7',
  tags: ['partner', 'onboarding', 'b2b'],
  complexity: 'intermediate',
  industryFit: ['lending', 'finance'],
  data: {
    intakeType: 'partner_onboarding',
    version: '1.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createWelcomeStep(),
      createCompanyProfileStep(),
      createLicensingStep(),
      createAdminAccountStep(),
      createReviewTermsStep(),
      createESignStep(),
      createCompleteStep(),
    ],
    complianceQuestions: [
      {
        id: 'terms-acceptance',
        question: 'I agree to the Partnership Agreement terms and conditions.',
        requiredAnswer: true,
        blockingMessage: 'You must agree to the terms to proceed.',
      },
      {
        id: 'info-accuracy',
        question: 'I certify that all information provided is accurate and complete.',
        requiredAnswer: true,
        blockingMessage: 'You must certify the accuracy of your information.',
      },
    ],
    exitBehavior: 'warn',
    showEstimatedTime: true,
    estimatedTimeMinutes: 10,
    successMessage: 'Partner account created successfully!',
  },
};

// =============================================================================
// V2 TEMPLATES: CONTRACT-DRIVEN PARTNER INVITATION SYSTEM
// =============================================================================

/**
 * V2 Step 3: Contract Package Selection
 * Multi-select contract types from business's enabled definitions
 */
const createContractSelectionStep = (): IntakeStepTemplate => ({
  id: 'step-contract-selection',
  order: 2,
  name: 'Contract Package',
  description: 'Select which contracts to include in this invitation',
  icon: 'LibraryAddCheck',
  requiresAuth: false,
  fields: [
    {
      id: 'field-selected-contracts',
      name: 'selectedContracts',
      label: 'Select Contracts',
      type: 'dynamic-select',
      required: false,
      helpText: 'Choose one or more contract types to bundle in this invitation',
      dynamicSelectConfig: {
        dataSource: 'contract-types',
        valueField: 'id',
        labelField: 'name',
        descriptionField: 'description',
        iconField: 'icon',
        multiple: true,
        emptyMessage: 'No contract types available. Please configure contract types first.',
        loadingMessage: 'Loading contract types...',
        managementLink: {
          href: '/control-center/document-templates',
          labelKey: 'contractPackage.manageTemplates',
        },
      },
      width: 12,
    },
  ],
});

/**
 * V2 Step 4: Contract Details
 * Dynamic sections per selected contract type, each rendering its fieldSchema.
 * The actual fields are injected at runtime based on Step 3 selections.
 */
const createContractDetailsStep = (): IntakeStepTemplate => ({
  id: 'step-contract-details',
  order: 3,
  name: 'Contract Details',
  description: 'Configure terms for each selected contract',
  icon: 'Article',
  requiresAuth: false,
  sections: [
    {
      id: 'section-contract-details-dynamic',
      title: 'Contract-Specific Terms',
      description: 'Complete the details below for each contract in the package',
      collapsible: true,
      dynamicSource: 'selectedContracts', // Sections generated per selected contract
    },
  ],
  fields: [
    {
      id: 'field-contract-details-placeholder',
      name: '_contractDetailsInfo',
      label: '',
      type: 'info-card',
      required: false,
      infoCardConfig: {
        variant: 'info',
        icon: 'Info',
        title: 'Contract-Specific Terms',
        description:
          'Each selected contract type has its own set of required terms. Complete the fields below for each contract in your package.',
      },
      width: 12,
    },
  ],
});

/**
 * V2 Step 5: Signing Counterparts
 * Per-contract B1 signer assignment
 */
const createSigningCounterpartsStep = (): IntakeStepTemplate => ({
  id: 'step-signing-counterparts',
  order: 4,
  name: 'Signing Counterparts',
  description: 'Assign who will sign each contract on your behalf',
  icon: 'HowToReg',
  requiresAuth: false,
  sections: [
    {
      id: 'section-signers-dynamic',
      title: 'Signer Assignments',
      description: 'Designate the authorized signer for each contract',
      collapsible: true,
      dynamicSource: 'selectedContracts', // One section per selected contract
    },
  ],
  fields: [
    {
      id: 'field-default-signer-user-id',
      name: 'defaultB1SignerUserId',
      label: 'Default Signer',
      type: 'dynamic-select',
      required: false,
      helpText: 'Pick a team member from your company; they will sign contracts unless overridden per contract.',
      dynamicSelectConfig: {
        dataSource: 'team-members',
        valueField: 'id',
        labelField: 'label',
        descriptionField: 'description',
        emptyMessage: 'No team members found. Invite teammates from the Team page first.',
        loadingMessage: 'Loading team members...',
      },
      width: 12,
    },
  ],
});

/**
 * V2 Step 8: Review (Extended)
 * Summary of all contracts in the package
 */
const createInviteReviewStepV2 = (): IntakeStepTemplate => ({
  id: 'step-review',
  order: 7,
  name: 'Review & Send',
  description: 'Review the complete invitation package before sending',
  icon: 'CheckCircle',
  requiresAuth: false,
  fields: [
    {
      id: 'field-summary-v2',
      name: '_summary',
      label: '',
      type: 'summary',
      required: false,
      summaryConfig: {
        sections: [
          {
            title: 'Partner Information',
            fields: [
              { name: 'invitedCompanyName', label: 'Company Name', format: 'text' },
              { name: 'invitedContactName', label: 'Contact Name', format: 'text' },
              { name: 'invitedEmail', label: 'Email', format: 'text' },
              { name: 'invitedPhone', label: 'Phone', format: 'text' },
            ],
          },
          {
            title: 'Partnership Type',
            fields: [
              {
                name: 'partnerType',
                label: 'Type',
                format: 'lookup',
                lookupMap: {
                  [PartnerType.REFERRAL]: 'Referral Partner',
                  [PartnerType.WHITE_LABEL]: 'White-Label Partner',
                },
              },
              {
                name: 'agreementType',
                label: 'Direction',
                format: 'lookup',
                lookupMap: {
                  ONE_WAY: 'One-Way',
                  TWO_WAY: 'Two-Way (Mutual)',
                },
              },
            ],
          },
          {
            title: 'Contract Package',
            fields: [{ name: 'selectedContracts', label: 'Contracts', format: 'list' }],
          },
          {
            title: 'Commission Terms',
            fields: [
              { name: 'commissionRate', label: 'Commission Rate', format: 'percentage' },
              {
                name: 'commissionTrigger',
                label: 'Earned When',
                format: 'lookup',
                lookupMap: {
                  SUBMISSION: 'Deal Submitted',
                  APPROVAL: 'Deal Approved',
                  FUNDING: 'Deal Funded',
                  FIRST_PAYMENT: 'First Payment',
                },
              },
              {
                name: 'paymentTerms',
                label: 'Payment',
                format: 'lookup',
                lookupMap: {
                  IMMEDIATE: 'Immediate',
                  NET_15: 'Net 15',
                  NET_30: 'Net 30',
                  NET_45: 'Net 45',
                  NET_60: 'Net 60',
                },
              },
            ],
          },
          {
            title: 'Agreement Terms',
            fields: [
              { name: 'effectiveDate', label: 'Effective', format: 'date' },
              { name: 'expirationDate', label: 'Expires', format: 'date' },
              { name: 'autoRenew', label: 'Auto-Renew', format: 'boolean' },
              {
                name: 'partnerVisibility',
                label: 'Visibility',
                format: 'lookup',
                lookupMap: {
                  STATS_ONLY: 'Statistics Only',
                  CASE_SUMMARY: 'Loan Summaries',
                  FULL_DETAILS: 'Full Loan Details',
                },
              },
            ],
          },
        ],
        showEditButtons: true,
      },
      width: 12,
    },
  ],
});

/**
 * V2 Partner Invitation Template (B1 Flow — 8 Steps)
 *
 * Steps:
 * 1. Partner Information (reuse)
 * 2. Partnership Type (reuse)
 * 3. Contract Package Selection (NEW)
 * 4. Contract Details (NEW — dynamic per contract)
 * 5. Signing Counterparts (NEW — per-contract signer)
 * 6. Commission Terms (reuse — conditional on FINANCIAL_CONTRACT_SUBTYPE_SLUGS)
 * 7. Agreement Terms (reuse)
 * 8. Review & Send (extended)
 */
export const invitePartnerTemplateV2: IntakeFlowTemplate = {
  id: `${PARTNER_INVITATION_TEMPLATE_IDS.INVITE_PARTNER}-v2`,
  name: 'Invite Partner (Contract Package)',
  description: 'Invite a partner with a bundled contract package (NDA, agreements, SLA, etc.)',
  category: 'partnerships',
  icon: 'PersonAdd',
  color: '#0078D7',
  tags: ['partner', 'invitation', 'contract', 'package', 'b2b'],
  complexity: 'advanced',
  industryFit: ['lending', 'finance'],
  data: {
    intakeType: 'partner_invitation',
    version: '2.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createPartnerInfoStep(), // Step 1 (order 0)
      createPartnershipTypeStep(), // Step 2 (order 1)
      createContractSelectionStep(), // Step 3 (order 2) — NEW
      createContractDetailsStep(), // Step 4 (order 3) — NEW
      createSigningCounterpartsStep(), // Step 5 (order 4) — NEW
      { ...createCommissionTermsStep(), order: 5 }, // Step 6 (reuse, reorder)
      { ...createAgreementTermsStep(), order: 6 }, // Step 7 (reuse, reorder)
      createInviteReviewStepV2(), // Step 8 (order 7) — extended
    ],
    complianceQuestions: [
      {
        id: 'authorization',
        question: 'I am authorized to invite partners on behalf of my company.',
        requiredAnswer: true,
        blockingMessage: 'You must be authorized to invite partners.',
      },
    ],
    exitBehavior: 'custom',
    customExitUrl: '/partnerships/invitations',
    showEstimatedTime: true,
    estimatedTimeMinutes: 8,
    successMessage: 'Partner invitation with contract package sent successfully!',
  },
};

// =============================================================================
// V2 B2 ONBOARDING STEPS
// =============================================================================

/**
 * V2 B2 Step 1: Welcome (Enhanced with contract package overview)
 */
const createWelcomeStepV2 = (): IntakeStepTemplate => ({
  id: 'step-welcome',
  order: 0,
  name: 'Welcome',
  description: 'Review your partnership invitation',
  icon: 'Celebration',
  requiresAuth: false,
  fields: [
    {
      id: 'field-welcome-message-v2',
      name: '_welcomeMessage',
      label: '',
      type: 'info-card',
      required: false,
      infoCardConfig: {
        variant: 'highlighted',
        icon: 'Handshake',
        title: "You've been invited to partner with {{invitingCompanyName}}",
        description:
          'This invitation includes a package of contracts for you to review and sign. Complete the steps below to set up your partner account.',
      },
      width: 12,
    },
    {
      id: 'field-terms-preview-v2',
      name: '_termsPreview',
      label: 'Partnership Terms',
      type: 'readonly-summary',
      required: false,
      readonlySummaryConfig: {
        fields: [
          { name: 'partnerType', label: 'Partnership Type' },
          { name: 'commissionRate', label: 'Commission Rate' },
          { name: 'paymentTerms', label: 'Payment Terms' },
        ],
      },
      width: 12,
    },
    {
      id: 'field-contract-package-preview',
      name: '_contractPackagePreview',
      label: 'Contract Package',
      type: 'readonly-summary',
      required: false,
      readonlySummaryConfig: {
        fields: [
          { name: 'contractCount', label: 'Contracts to Sign' },
          { name: 'contractList', label: 'Included Contracts' },
        ],
      },
      width: 12,
    },
  ],
});

/**
 * V2 B2 Step 5: Contract Review
 * Read-only summary per contract with key terms
 */
const createContractReviewStep = (): IntakeStepTemplate => ({
  id: 'step-contract-review',
  order: 4,
  name: 'Review Contracts',
  description: 'Review all contracts in the invitation package',
  icon: 'Gavel',
  requiresAuth: false,
  sections: [
    {
      id: 'section-contract-review-dynamic',
      title: 'Contract Details',
      description: 'Review the terms of each contract below',
      collapsible: true,
      dynamicSource: 'contracts', // Sections generated per invitation contract
    },
  ],
  fields: [
    {
      id: 'field-contract-review-info',
      name: '_contractReviewInfo',
      label: '',
      type: 'info-card',
      required: false,
      infoCardConfig: {
        variant: 'info',
        icon: 'Gavel',
        title: 'Contract Package Review',
        description:
          'Review each contract below. You will assign signers in the next step and sign electronically after that.',
      },
      width: 12,
    },
  ],
});

/**
 * V2 B2 Step 6: Signer Assignment
 * B2 assigns signers per contract
 */
const createB2SignerAssignmentStep = (): IntakeStepTemplate => ({
  id: 'step-signer-assignment',
  order: 5,
  name: 'Assign Signers',
  description: 'Designate who will sign each contract on your behalf',
  icon: 'HowToReg',
  requiresAuth: false,
  sections: [
    {
      id: 'section-b2-signers-dynamic',
      title: 'Signer Assignments',
      description: 'Assign the authorized signer for each contract',
      collapsible: true,
      dynamicSource: 'contracts', // Sections generated per invitation contract
    },
  ],
  fields: [
    {
      id: 'field-b2-default-signer-info',
      name: '_b2SignerInfo',
      label: '',
      type: 'info-card',
      required: false,
      infoCardConfig: {
        variant: 'info',
        icon: 'Person',
        title: 'Signer Assignment',
        description:
          'By default, your admin account details are used. Override per contract if different people need to sign.',
      },
      width: 12,
    },
    {
      id: 'field-b2-use-admin-as-signer',
      name: 'useAdminAsSigner',
      label: 'Use my account details as the default signer for all contracts',
      type: 'checkbox',
      required: false,
      defaultValue: true,
      width: 12,
    },
  ],
});

/**
 * V2 B2 Step 7: Multi-Contract E-Sign
 * Sequential signing with checklist status
 */
const createMultiContractESignStep = (): IntakeStepTemplate => ({
  id: 'step-esign',
  order: 6,
  name: 'Sign Contracts',
  description: 'Sign all contracts in the package electronically',
  icon: 'Draw',
  requiresAuth: false,
  sections: [
    {
      id: 'section-esign-dynamic',
      title: 'Contract Signing',
      description: 'Sign each contract below. Complete them in order.',
      collapsible: false,
      dynamicSource: 'contracts', // One signing embed per contract
    },
  ],
  fields: [
    {
      id: 'field-signing-progress',
      name: '_signingProgress',
      label: '',
      type: 'info-card',
      required: false,
      infoCardConfig: {
        variant: 'info',
        icon: 'Draw',
        title: 'Electronic Signing',
        description:
          'Sign each contract below. After all contracts are signed, you will be automatically advanced to the next step.',
      },
      width: 12,
    },
  ],
});

/**
 * V2 Partner Onboarding Template (B2 Flow — 8 Steps)
 *
 * Steps:
 * 1. Welcome (enhanced with contract package overview)
 * 2. Company Profile (reuse)
 * 3. Licensing (reuse, conditional on NMLS)
 * 4. Admin Account (reuse)
 * 5. Contract Review (NEW — read-only summary per contract)
 * 6. Signer Assignment (NEW — B2 assigns signers per contract)
 * 7. Multi-Contract E-Sign (ENHANCED — iterates over contracts)
 * 8. Complete (reuse)
 */
export const partnerOnboardingTemplateV2: IntakeFlowTemplate = {
  id: `${PARTNER_INVITATION_TEMPLATE_IDS.PARTNER_ONBOARDING}-v2`,
  name: 'Partner Onboarding (Contract Package)',
  description: 'Complete your partner account setup with multi-contract signing',
  category: 'onboarding',
  icon: 'Handshake',
  color: '#0078D7',
  tags: ['partner', 'onboarding', 'contract', 'package', 'b2b'],
  complexity: 'advanced',
  industryFit: ['lending', 'finance'],
  data: {
    intakeType: 'partner_onboarding',
    version: '2.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createWelcomeStepV2(), // Step 1 (order 0)
      { ...createCompanyProfileStep(), order: 1 }, // Step 2 (reuse)
      { ...createLicensingStep(), order: 2 }, // Step 3 (reuse, conditional)
      { ...createAdminAccountStep(), order: 3 }, // Step 4 (reuse)
      createContractReviewStep(), // Step 5 (order 4) — NEW
      createB2SignerAssignmentStep(), // Step 6 (order 5) — NEW
      createMultiContractESignStep(), // Step 7 (order 6) — ENHANCED
      { ...createCompleteStep(), order: 7 }, // Step 8 (reuse)
    ],
    complianceQuestions: [
      {
        id: 'terms-acceptance',
        question: 'I agree to the Partnership Agreement terms and conditions.',
        requiredAnswer: true,
        blockingMessage: 'You must agree to the terms to proceed.',
      },
      {
        id: 'info-accuracy',
        question: 'I certify that all information provided is accurate and complete.',
        requiredAnswer: true,
        blockingMessage: 'You must certify the accuracy of your information.',
      },
    ],
    exitBehavior: 'warn',
    showEstimatedTime: true,
    estimatedTimeMinutes: 15,
    successMessage: 'Partner account created and contracts signed successfully!',
  },
};

// =============================================================================
// EXPORTS
// =============================================================================

/**
 * All partner invitation templates (V1 + V2)
 */
export const partnerInvitationTemplates: IntakeFlowTemplate[] = [
  invitePartnerTemplate,
  partnerOnboardingTemplate,
  invitePartnerTemplateV2,
  partnerOnboardingTemplateV2,
];
